import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { PaymentWindowContainer, PaymentWindowNetsCallback } from '@sdir/payment-window';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { routes } from '../../../constants/routes';
import { Card } from '../../Atoms';

const PaymentWindowModule: React.FC<{
  errorCallback: React.Dispatch<React.SetStateAction<boolean>>;
  cancelCallback: () => void;
  successCallback: () => void;
  chargeId: number;
  isExternalUser?: boolean;
}> = ({ errorCallback, cancelCallback, successCallback, chargeId, isExternalUser = false }) => {
  const PaymentWindowStepSession = 'payment_window_step';
  const ApplicationPaymentStepUrlSession = 'application_payment_step_url';
  const [step, setStep] = useState(window.sessionStorage.getItem(PaymentWindowStepSession) ?? '1');
  const location = useLocation();

  const { transactionId, responseCode } = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      transactionId: search.get('transactionId') || '',
      responseCode: search.get('responseCode') || '',
    };
  }, [location]);

  const intl = useIntl();
  const [error, setError] = useState('');

  const currentUser = useCurrentUser();
  const [token, setToken] = useState(currentUser?.access_token || '');
  const selectedIds = chargeId ? [chargeId] : [];

  const representationId = useMemo(() => {
    if (isExternalUser) {
      return currentUser?.profile?.externalUserId;
    }
    return currentUser?.profile?.reportee;
  }, [currentUser?.profile?.reportee, isExternalUser]);

  useEffect(() => {
    setToken(currentUser?.access_token as string);
  }, [currentUser]);

  useEffect(() => {
    window.sessionStorage.setItem(PaymentWindowStepSession, step);
  }, [step]);

  const beforeRedirect = useCallback(() => {
    setStep('2');
  }, []);

  useEffect(() => {
    if (!responseCode || !transactionId) {
      setStep('1');
    }
  }, [responseCode, transactionId]);

  const goBackToFirstStep = useCallback(() => {
    setStep('1');
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    window.sessionStorage.setItem(ApplicationPaymentStepUrlSession, location.pathname);
  }, []);

  const netsRedirectUrl = useMemo(() => {
    return `${window.location.origin + window.location.pathname}#${routes.paymentWindow}`;
  }, [window?.location]);

  const onProcessFailCallback = useCallback(() => {
    setError(intl.formatMessage({ id: 'newapp.steps.payment.failed' }));
    errorCallback(true);
  }, [intl, errorCallback]);

  return (
    <div>
      {error ? (
        <ErrorCard>
          <p>{error}</p>
        </ErrorCard>
      ) : (
        <>
          {step === '1' && token && (
            <PaymentWindowContainer
              unpaidChargesIds={selectedIds}
              beforeRedirect={beforeRedirect}
              token={token}
              netsRedirectUrl={netsRedirectUrl}
              goBackToPortal={cancelCallback}
              goBackButtonText={intl.formatMessage({ id: 'newapp.steps.payment.complete' })}
              representationId={representationId}
              externalUser={isExternalUser}
              useNewDesign
              language={intl.locale === 'en' ? 2 : 1}
            />
          )}
          {step === '2' && token && (
            <PaymentWindowNetsCallback
              goBackToFirstStep={goBackToFirstStep}
              goBackToPortal={successCallback}
              netsResponseCode={responseCode}
              netsTransactionId={transactionId}
              token={token}
              representationId={representationId}
              unpaidChargesIds={selectedIds}
              externalUser={isExternalUser}
              langauge={intl.locale === 'en' ? 2 : 1}
              goBackButtonText={intl.formatMessage({ id: 'newapp.steps.payment.complete' })}
              onProcessFailCallback={onProcessFailCallback}
            />
          )}
        </>
      )}
    </div>
  );
};

const ErrorCard = styled(Card)`
  display: flex;
  justify-content: center;
`;

export default PaymentWindowModule;
